import { Back, type IconGlyph } from "@/icons";
import { cn } from "@/lib/cn";
import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import type { To } from "react-router-dom";
import { Button, ButtonLink } from "./Button";
import { Icon } from "./Icon";
import { PageContainer, type PageContainerProps } from "./Layout";
import { SurfaceProvider } from "./Surface";
import { Heading } from "./Typography";

type BackAffordance =
  | { backTo: To; onBack?: never }
  | { onBack: () => void; backTo?: never }
  | { backTo?: undefined; onBack?: undefined };

type HeaderIdentity =
  | { title: ReactNode; identity?: never }
  | { identity: ReactNode; title?: never };

/**
 * Props of `PageHeader`; `backTo` navigates, `onBack` runs a handler, neither hides the back button.
 * `identity` replaces the h1 title (the screen then owns its h1); `bottom` renders under the row (a stepper).
 */
export type PageHeaderProps = Omit<HTMLAttributes<HTMLElement>, "title" | "children"> &
  BackAffordance &
  HeaderIdentity & {
    icon?: IconGlyph;
    trailing?: ReactNode;
    bottom?: ReactNode;
    variant?: "bar" | "plain";
    surface?: "header" | "card";
    sticky?: boolean;
    raised?: boolean;
    offsetActiveOrderBar?: boolean;
    gutter?: PageContainerProps["gutter"];
    backLabel?: string;
  };

const ACTIVE_ORDER_BAR_OFFSET = { top: "var(--active-order-bar-height, 0px)" } as const;

/** Screen header: back affordance, title (or identity) with optional icon, a trailing slot and a bottom slot, as a sticky bar or plain row. */
export const PageHeader = forwardRef<HTMLElement, PageHeaderProps>(function PageHeader(
  {
    title,
    identity,
    icon,
    trailing,
    bottom,
    backTo,
    onBack,
    variant = "bar",
    surface = "header",
    sticky,
    raised = false,
    offsetActiveOrderBar = false,
    gutter,
    backLabel = "Voltar",
    className,
    style,
    ...rest
  },
  ref,
) {
  const isBar = variant === "bar";
  const isSticky = sticky ?? isBar;
  const backGlyph = <Icon as={Back} size="xl" weight="bold" tone="primary" />;

  return (
    <header
      {...rest}
      ref={ref}
      style={offsetActiveOrderBar ? { ...ACTIVE_ORDER_BAR_OFFSET, ...style } : style}
      className={cn(
        isSticky && (raised ? "sticky z-sticky-raised" : "sticky z-sticky"),
        isSticky && !offsetActiveOrderBar && "top-0",
        isBar && (surface === "card" ? "bg-card shadow-bar" : "bg-header-gray shadow-bar"),
        isBar && !bottom && "flex h-header items-center",
        !isBar && "py-4",
        className,
      )}
    >
      <PageContainer
        gutter={gutter}
        surface="card"
        className={cn("flex min-w-0 flex-1 items-center gap-2", isBar && bottom && "h-header")}
      >
        {backTo !== undefined && (
          <ButtonLink to={backTo} variant="ghost" tone="neutral" size="icon-bar" aria-label={backLabel}>
            {backGlyph}
          </ButtonLink>
        )}
        {onBack && (
          <Button variant="ghost" tone="neutral" size="icon-bar" aria-label={backLabel} onClick={onBack}>
            {backGlyph}
          </Button>
        )}
        {icon && <Icon as={icon} size="3xl" tone="primary" />}
        {identity !== undefined ? (
          <div className="flex min-w-0 flex-1 items-center gap-3">{identity}</div>
        ) : (
          <Heading level={1} size="title" truncate className="min-w-0 flex-1">
            {title}
          </Heading>
        )}
        {trailing && <div className="flex shrink-0 items-center gap-2">{trailing}</div>}
      </PageContainer>
      {bottom ? <SurfaceProvider surface="card">{bottom}</SurfaceProvider> : null}
    </header>
  );
});
